import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Trash } from "iconsax-react";
import { FileUploader } from "@/shared/components";
import { Button, DatePicker, Input, Select, ToggleButton } from "@/shared/ui";
import { type Locale } from "@/shared/utils";
import { SOCIAL_CATEGORY_OPTIONS } from "@/features/hr/hiring/consts/socialCategory";
import type { SocialCategoryRecord } from "../../../types/personalSheet";
import { editSocialCategoryEntrySchema } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  records: SocialCategoryRecord[];
  initialHasNoSocialCategories?: boolean;
  recordsEditable?: boolean;
  draftAddedRecords?: SocialCategoryRecord[];
}

interface SocialCategoryEntry {
  id: number | null;
  category_type: string;
  issue_date: string;
  expiry_date: string;
  notes: string;
  existingFileUrl: string | null;
  newFile: File | null;
  fileRemoved: boolean;
  fileToken: string | null;
}

function makeEmptyEntry(): SocialCategoryEntry {
  return {
    id: null,
    category_type: "",
    issue_date: "",
    expiry_date: "",
    notes: "",
    existingFileUrl: null,
    newFile: null,
    fileRemoved: false,
    fileToken: null,
  };
}

function fromRecord(record: SocialCategoryRecord): SocialCategoryEntry {
  return {
    id: record.id,
    category_type: record.category_type,
    issue_date: record.issue_date ?? "",
    expiry_date: record.expiry_date ?? "",
    notes: record.notes,
    existingFileUrl: record.document_file_url,
    newFile: null,
    fileRemoved: false,
    fileToken: record.document_file_token ?? null,
  };
}

function isFilledEntry(entry: SocialCategoryEntry): boolean {
  return entry.category_type.trim() !== "";
}

function toApiEntry(entry: SocialCategoryEntry): Record<string, unknown> {
  const api: Record<string, unknown> = {
    category_type: entry.category_type,
    issue_date: entry.issue_date,
    expiry_date: entry.expiry_date || null,
    notes: entry.notes,
  };
  if (entry.id != null) api.id = entry.id;
  if (entry.newFile && entry.fileToken) api.document_file_token = entry.fileToken;
  else if (entry.fileRemoved) api.document_file = null;
  else if (entry.fileToken) api.document_file_token = entry.fileToken;
  return api;
}

function fileSignature(entry: SocialCategoryEntry): string {
  if (entry.newFile) return `new:${entry.fileToken}`;
  if (entry.fileRemoved) return "removed";
  return entry.existingFileUrl ? "keep" : "none";
}

function entriesSignature(entries: SocialCategoryEntry[]): string {
  return JSON.stringify(
    entries.filter(isFilledEntry).map((e) => ({
      id: e.id,
      category_type: e.category_type,
      issue_date: e.issue_date,
      expiry_date: e.expiry_date,
      notes: e.notes,
      file: fileSignature(e),
    })),
  );
}

const EditSocialCategoriesSection = forwardRef<SectionFormHandle, Props>(function EditSocialCategoriesSection(
  { records, initialHasNoSocialCategories, recordsEditable = false, draftAddedRecords },
  ref,
) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as Locale) || "ru";

  const [hasNoSocialCategories, setHasNoSocialCategories] = useState(initialHasNoSocialCategories ?? false);
  const [newEntries, setNewEntries] = useState<SocialCategoryEntry[]>(() =>
    recordsEditable ? records.map(fromRecord) : (draftAddedRecords ?? []).map(fromRecord),
  );
  const initialSignatureRef = useRef<string>(entriesSignature(recordsEditable ? records.map(fromRecord) : []));
  const fileTokenRef = useRef(0);
  const [existingFileChanges, setExistingFileChanges] = useState<Record<number, File>>({});
  const [removedExistingFiles, setRemovedExistingFiles] = useState<Set<number>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});

  const categoryOptions = SOCIAL_CATEGORY_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(`personalSheet.socialCategoryValues.${opt.labelKey}`),
  }));

  function handleAddEntry() {
    setNewEntries((prev) => [...prev, makeEmptyEntry()]);
  }

  function handleRemoveEntry(index: number) {
    setNewEntries((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function updateEntry(index: number, field: "category_type" | "issue_date" | "expiry_date" | "notes", value: string) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  }

  function setEntryFile(index: number, file: File | null) {
    const token = file ? `f${(fileTokenRef.current += 1)}` : null;
    setNewEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, newFile: file, fileToken: token, fileRemoved: file ? false : entry.fileRemoved } : entry,
      ),
    );
  }

  function setEntryFileRemoved(index: number) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, fileRemoved: true } : entry)));
  }

  function updateExistingFile(recordIndex: number, file: File | null) {
    setExistingFileChanges((prev) => {
      if (file === null) {
        const next = { ...prev };
        delete next[recordIndex];
        return next;
      }
      return { ...prev, [recordIndex]: file };
    });
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        if (hasNoSocialCategories) return [] as unknown as Record<string, unknown>;
        if (recordsEditable) {
          if (entriesSignature(newEntries) === initialSignatureRef.current) return null;
          return newEntries.filter(isFilledEntry).map(toApiEntry) as unknown as Record<string, unknown>;
        }
        const hasNewFilledEntries = newEntries.some(isFilledEntry);
        const hasExistingFileChanges = Object.keys(existingFileChanges).length > 0;
        if (!hasNewFilledEntries && !hasExistingFileChanges) return null;
        const allRecords = [
          ...records.map((r) => ({
            category_type: r.category_type,
            issue_date: r.issue_date ?? "",
            expiry_date: r.expiry_date || null,
            notes: r.notes,
          })),
          ...newEntries.filter(isFilledEntry).map((e) => ({
            category_type: e.category_type,
            issue_date: e.issue_date,
            expiry_date: e.expiry_date || null,
            notes: e.notes,
          })),
        ];
        return allRecords as unknown as Record<string, unknown>;
      },
      getFiles() {
        if (hasNoSocialCategories) return null;
        const files: Record<string, File> = {};
        if (recordsEditable) {
          newEntries.filter(isFilledEntry).forEach((entry) => {
            if (entry.newFile && entry.fileToken) {
              files[`social_categories.${entry.fileToken}.document_file`] = entry.newFile;
            }
          });
        } else {
          for (const [idx, file] of Object.entries(existingFileChanges)) {
            files[`social_categories.${idx}.document_file`] = file;
          }
          newEntries.forEach((entry, i) => {
            if (entry.newFile) {
              files[`social_categories.${records.length + i}.document_file`] = entry.newFile;
            }
          });
        }
        return Object.keys(files).length > 0 ? files : null;
      },
      async validate() {
        if (hasNoSocialCategories) {
          setValidationErrors({});
          return true;
        }
        const errors: Record<number, Record<string, string>> = {};
        let allValid = true;
        for (let i = 0; i < newEntries.length; i++) {
          const entry = newEntries[i];
          if (!isFilledEntry(entry)) continue;
          const entryErrors: Record<string, string> = {};
          const result = editSocialCategoryEntrySchema.safeParse({
            category_type: entry.category_type,
            issue_date: entry.issue_date,
            expiry_date: entry.expiry_date,
            notes: entry.notes,
          });
          if (!result.success) {
            for (const issue of result.error.issues) {
              entryErrors[issue.path[0] as string] = issue.message;
            }
          }
          const fileOk = recordsEditable
            ? (!!entry.existingFileUrl && !entry.fileRemoved) || !!entry.newFile
            : !!entry.newFile || ((!!entry.fileToken || !!entry.existingFileUrl) && !entry.fileRemoved);
          if (!fileOk) entryErrors.document_file = "personalSheet.validation.required";
          if (Object.keys(entryErrors).length > 0) {
            allValid = false;
            errors[i] = entryErrors;
          }
        }
        setValidationErrors(errors);
        return allValid;
      },
      isDirty() {
        const flagChanged = hasNoSocialCategories !== (initialHasNoSocialCategories ?? false);
        if (recordsEditable) return flagChanged || entriesSignature(newEntries) !== initialSignatureRef.current;
        return flagChanged || newEntries.some(isFilledEntry) || Object.keys(existingFileChanges).length > 0;
      },
      getFlagChanges() {
        if (hasNoSocialCategories !== (initialHasNoSocialCategories ?? false)) {
          return { has_no_social_categories: hasNoSocialCategories };
        }
        return null;
      },
    };
  });

  const showReadOnly = !recordsEditable;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <span className="text-label-sm content-base-secondary">{t("personalSheet.hasNoSocialCategories")}</span>
        <ToggleButton checked={hasNoSocialCategories} onChange={setHasNoSocialCategories} />
      </div>

      {hasNoSocialCategories ? (
        <p className="text-body-regular-sm content-base-secondary p-3 surface-secondary-fill radius-sm">
          {t("personalSheet.noSocialCategoriesNote")}
        </p>
      ) : (
        <>
          {showReadOnly &&
            records.map((record, index) => (
              <div key={record.id} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
                <p className="text-body-bold-md content-base-primary">
                  {t("personalSheet.socialCategoryRecord", { number: index + 1 })}
                </p>
                <Select
                  label={t("personalSheet.fields.categoryType")}
                  options={categoryOptions}
                  value={record.category_type}
                  onChange={() => {}}
                  placeholder=""
                  disabled
                />
                <FileUploader
                  label={t("personalSheet.fields.documentFile")}
                  existingFileUrl={removedExistingFiles.has(index) ? null : record.document_file_url}
                  value={existingFileChanges[index] ?? null}
                  onChange={(file) => updateExistingFile(index, file)}
                  onRemoveExisting={() => setRemovedExistingFiles((prev) => new Set(prev).add(index))}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePicker
                    label={t("personalSheet.fields.issueDate")}
                    mode="single"
                    locale={locale}
                    value={record.issue_date ? new Date(record.issue_date) : null}
                    onChange={() => {}}
                    disabled
                  />
                  <DatePicker
                    label={t("personalSheet.fields.expiryDate")}
                    mode="single"
                    locale={locale}
                    value={record.expiry_date ? new Date(record.expiry_date) : null}
                    onChange={() => {}}
                    disabled
                  />
                </div>
                <Input label={t("personalSheet.fields.notes")} placeholder="" value={record.notes} disabled />
              </div>
            ))}

          {newEntries.map((entry, index) => (
            <div key={index} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-body-bold-md content-base-primary">
                  {t("personalSheet.socialCategoryRecord", { number: (showReadOnly ? records.length : 0) + index + 1 })}
                </p>
                <Button variant="danger" size="sm" onClick={() => handleRemoveEntry(index)}>
                  <Trash size={16} color="currentColor" />
                  {t("personalSheet.removeRecord")}
                </Button>
              </div>

              <Select
                label={t("personalSheet.fields.categoryType")}
                hasRequiredLabel
                options={categoryOptions}
                value={entry.category_type}
                onChange={(v) => updateEntry(index, "category_type", v ?? "")}
                placeholder={t("personalSheet.fields.categoryType")}
                error={validationErrors[index]?.category_type ? t(validationErrors[index].category_type) : undefined}
              />

              <FileUploader
                label={t("personalSheet.fields.documentFile")}
                hasRequiredLabel
                existingFileUrl={entry.fileRemoved ? null : entry.existingFileUrl}
                value={entry.newFile}
                onChange={(file) => setEntryFile(index, file)}
                onRemoveExisting={() => setEntryFileRemoved(index)}
                error={validationErrors[index]?.document_file ? t(validationErrors[index].document_file) : undefined}
                namespace="EmployeePortal"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DatePicker
                  label={t("personalSheet.fields.issueDate")}
                  hasRequiredLabel
                  locale={locale}
                  value={entry.issue_date ? new Date(entry.issue_date) : null}
                  onChange={(d) => updateEntry(index, "issue_date", d ? d.toISOString().split("T")[0] : "")}
                  clearable
                  error={validationErrors[index]?.issue_date ? t(validationErrors[index].issue_date) : undefined}
                />
                <DatePicker
                  label={t("personalSheet.fields.expiryDate")}
                  locale={locale}
                  value={entry.expiry_date ? new Date(entry.expiry_date) : null}
                  onChange={(d) => updateEntry(index, "expiry_date", d ? d.toISOString().split("T")[0] : "")}
                  clearable
                />
              </div>

              <Input
                label={t("personalSheet.fields.notes")}
                placeholder={t("personalSheet.fields.notesPlaceholder")}
                isTextarea
                value={entry.notes}
                onChange={(e) => updateEntry(index, "notes", e.target.value)}
              />
            </div>
          ))}

          <Button variant="tertiary" size="lg" onClick={handleAddEntry} className="w-full text-center">
            <Add size={16} color="currentColor" />
            {t("personalSheet.addRecord")}
          </Button>
        </>
      )}
    </div>
  );
});

export default EditSocialCategoriesSection;
